/**
 * OpenRouter provider adapter — multi-model gateway.
 * All requests proxy through the sidecar (desktop) or Vercel edge function (web).
 * API keys are never exposed client-side.
 */

import type {
  AIProviderAdapter,
  AICompletionRequest,
  AICompletionResponse,
} from './base-provider';
import { getApiBase } from '@/app/api-router';

const MODELS_URL = 'https://openrouter.ai/api/v1/models';
const COMPLETION_TIMEOUT_MS = 30_000;
const DISCOVERY_TIMEOUT_MS = 5_000;
const HEALTH_CHECK_TIMEOUT_MS = 5_000;
const MAX_CONSECUTIVE_FAILURES = 3;
const DEFAULT_MODEL = 'anthropic/claude-haiku-4-5-20251001';

export class OpenRouterProvider implements AIProviderAdapter {
  readonly id = 'openrouter';
  readonly name = 'OpenRouter';
  readonly supportsStructuredOutput = true;
  readonly supportsGeneration = true;

  private consecutiveFailures = 0;

  async isAvailable(): Promise<boolean> {
    // After N consecutive failures, report unavailable until TTL expires
    if (this.consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
      return false;
    }

    // Lightweight health check: HEAD request to the proxy endpoint
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), HEALTH_CHECK_TIMEOUT_MS);
      const res = await fetch(`${getApiBase()}/api/ai/openrouter`, {
        method: 'HEAD',
        signal: controller.signal,
      });
      clearTimeout(timer);
      // 405 (Method Not Allowed) is fine — it means the endpoint exists
      return res.ok || res.status === 405;
    } catch {
      return false;
    }
  }

  async listModels(): Promise<string[]> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), DISCOVERY_TIMEOUT_MS);

    try {
      const res = await fetch(MODELS_URL, { signal: controller.signal });
      clearTimeout(timer);

      if (!res.ok) return [DEFAULT_MODEL];

      const data = (await res.json()) as {
        data?: { id: string; context_length?: number }[];
      };

      if (!data.data) return [DEFAULT_MODEL];

      // Return top 20 chat models sorted by context length
      return data.data
        .filter(m => m.id)
        .sort((a, b) => (b.context_length ?? 0) - (a.context_length ?? 0))
        .slice(0, 20)
        .map(m => m.id);
    } catch {
      clearTimeout(timer);
      return [DEFAULT_MODEL];
    }
  }

  async complete(request: AICompletionRequest): Promise<AICompletionResponse> {
    const model = request.model ?? DEFAULT_MODEL;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), COMPLETION_TIMEOUT_MS);
    const start = performance.now();

    const reqBody: Record<string, unknown> = {
      model,
      max_tokens: request.maxTokens ?? 512,
      temperature: request.temperature ?? 0.3,
      messages: [
        ...(request.systemPrompt
          ? [{ role: 'system' as const, content: request.systemPrompt }]
          : []),
        { role: 'user' as const, content: request.prompt },
      ],
    };

    if (request.outputSchema) {
      reqBody.response_format = {
        type: 'json_schema',
        json_schema: { name: 'output', schema: request.outputSchema, strict: true },
      };
    }

    const body = JSON.stringify(reqBody);

    try {
      // Route through sidecar (desktop) or Vercel edge function (web)
      const res = await fetch(`${getApiBase()}/api/ai/openrouter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        signal: controller.signal,
      });

      clearTimeout(timer);

      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        throw new Error(`OpenRouter returned ${res.status}: ${errText}`);
      }

      const data = (await res.json()) as {
        choices?: { message: { content: string } }[];
        usage?: { prompt_tokens: number; completion_tokens: number };
      };

      const text = data.choices?.[0]?.message?.content ?? '';

      // Reset failure counter on success
      this.consecutiveFailures = 0;

      const trimmedText = text.trim();
      const result: AICompletionResponse = {
        text: trimmedText,
        model,
        provider: this.id,
        tokensUsed: data.usage
          ? data.usage.prompt_tokens + data.usage.completion_tokens
          : undefined,
        latencyMs: Math.round(performance.now() - start),
      };

      if (request.outputSchema && trimmedText) {
        try {
          result.structuredOutput = JSON.parse(trimmedText);
        } catch {
          // Fall through to text-only response
        }
      }

      return result;
    } catch (err) {
      clearTimeout(timer);
      this.consecutiveFailures++;
      throw err;
    }
  }

  async validate(): Promise<{ valid: boolean; error?: string }> {
    try {
      const result = await this.complete({
        prompt: 'ping',
        maxTokens: 5,
      });
      return result.text.length > 0
        ? { valid: true }
        : { valid: false, error: 'Empty response from OpenRouter' };
    } catch (err) {
      return {
        valid: false,
        error: err instanceof Error ? err.message : 'Validation failed',
      };
    }
  }
}
